/**
 * Sixes cell-movement engine (spec v5).
 *
 * Buckets do not move until they cross a threshold. When they do, they claim — in completion
 * order, first come first served — one line from a fixed catalog and slide their winners onto it,
 * swapping out whatever occupies each destination slot (the universal swap rule).
 *
 * Bucket families (by cell count on the grid):
 *   - 4-draw (4 cells, rook-placed one-per-row in outer rows 0,1,4,5): complete at 4/4 and claim,
 *     in order, COL2 → COL3 → DIAG_TLBR → DIAG_TRBL (5th = jackpot, no geometric line yet).
 *   - 6-draw (6 cells): at 4/6 claim a row (ROW2 → ROW3); at 6/6 upgrade to a full unaided
 *     column (COL0_FULL → COL5_FULL).
 *
 * Coordinates are 0-indexed market-cell indices (centre 2×2 multiplier excluded — it is the
 * "wild" that completes any line passing through it).
 *
 * This module is the single source of truth shared by set counting and the live grid animation.
 * It is mirrored byte-for-logic in `backend/utils/sixesMovement.js`.
 */
import { marketCellGridPosition } from "./sixesGridLayout";
import { isSixesCellWon, type SixesCellOutcome } from "./sixesLiveData";
import { SIXES_BUCKETS, type SixesBucket } from "./sixesMarketBucket";

export type SixesBucketMap = readonly (SixesBucket | null)[];

const SIXES_RUNS_SIDE_BUCKETS: readonly SixesBucket[] = ["team_a_runs", "team_b_runs"];
const SIXES_PLAYER_SIDE_BUCKETS: readonly SixesBucket[] = ["team_a_player", "team_b_player"];
const SIXES_OUTER_GRID_ROWS = new Set([0, 1, 4, 5]);

function mergedSideClaimKind(
    mergedWon: readonly number[],
): SixesClaimKind {
    const allOuter = mergedWon.every((idx) => SIXES_OUTER_GRID_ROWS.has(marketCellGridPosition(idx).row));
    return allOuter ? "four_draw" : "six_draw_row";
}

function mergedRunsClaimKind(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
): SixesClaimKind {
    return mergedSideClaimKind(mergedRunsWonCellIndices(buckets, outcomes));
}

/** Primary bucket id used when both runs sides share the board (legacy adaptive rosters). */
export const SIXES_MERGED_RUNS_SLIDE_BUCKET: SixesBucket = "team_a_runs";
/** Primary bucket id when both player sides share the board (same colour, one combined line). */
export const SIXES_MERGED_PLAYER_SLIDE_BUCKET: SixesBucket = "team_a_player";

/** A 4-draw bucket completes (and claims a line) at 4/4 won; a 6-draw claims a row at 4/6. */
export const SIXES_BUCKET_SLIDE_MIN_WINS = 4;
/** A 6-draw bucket upgrades its row claim to a full column at 6/6 won. */
export const SIXES_SIX_DRAW_FULL_WINS = 6;

export type SixesLineId =
    | "COL2"
    | "COL3"
    | "DIAG_TLBR"
    | "DIAG_TRBL"
    | "ROW2"
    | "ROW3"
    | "COL0_FULL"
    | "COL5_FULL";

/** Target market-cell indices per line (0-indexed; wild fills any internal gap). */
export const SIXES_LINE_TARGETS: Record<SixesLineId, readonly number[]> = {
    COL2: [2, 8, 22, 28],
    COL3: [3, 9, 23, 29],
    DIAG_TLBR: [0, 7, 24, 31],
    DIAG_TRBL: [5, 10, 21, 26],
    ROW2: [12, 13, 14, 15],
    ROW3: [16, 17, 18, 19],
    COL0_FULL: [0, 6, 12, 16, 20, 26],
    COL5_FULL: [5, 11, 15, 19, 25, 31],
};

/** 4-draw lines in claim order (first 4-draw bucket to complete → COL2, etc.). */
export const SIXES_FOUR_DRAW_LINE_ORDER: readonly SixesLineId[] = ["COL2", "COL3", "DIAG_TLBR", "DIAG_TRBL"];
/** 6-draw row lines in claim order (first 6-draw bucket to hit 4/6 → ROW2, etc.). */
export const SIXES_SIX_DRAW_ROW_ORDER: readonly SixesLineId[] = ["ROW2", "ROW3"];
/** 6-draw full-column lines in claim order (first 6-draw bucket to hit 6/6 → COL0_FULL, etc.). */
export const SIXES_SIX_DRAW_COL_ORDER: readonly SixesLineId[] = ["COL0_FULL", "COL5_FULL"];

export type SixesClaimKind = "four_draw" | "six_draw_row" | "six_draw_col";

/** A directional content move: the cell currently at slot `from` slides to slot `to`. */
export type SixesSlideSwap = { from: number; to: number };

/** A bucket's resolved claim on a line, with the slot-level swaps that realise the slide. */
export type SixesLineClaim = {
    bucket: SixesBucket;
    lineId: SixesLineId;
    kind: SixesClaimKind;
    /** Completion rank used to order claims (lower = earlier). */
    rank: number;
    /** Slot-level swaps for this claim, applied in order against the evolving layout. */
    swaps: SixesSlideSwap[];
};

export type SixesMovementPlan = {
    claims: SixesLineClaim[];
    /** True when a 5th 4-draw bucket has completed — board-level jackpot (no geometric line). */
    jackpot: boolean;
};

/** Deterministic 0..1 RNG. Server passes a seeded RNG; set counting passes none (canonical order). */
export type SixesRng = () => number;

export function bucketCellIndices(buckets: SixesBucketMap, bucket: SixesBucket): number[] {
    const out: number[] = [];
    buckets.forEach((b, i) => {
        if (b === bucket) out.push(i);
    });
    return out;
}

export function bucketWonCellIndices(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
    bucket: SixesBucket,
): number[] {
    return bucketCellIndices(buckets, bucket).filter((idx) => isSixesCellWon(outcomes[idx] ?? "pending"));
}

export function bucketWonCount(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
    bucket: SixesBucket,
): number {
    return bucketWonCellIndices(buckets, outcomes, bucket).length;
}

/** A bucket is a 6-draw bucket when it has 6 cells on the grid (else 4-draw). */
export function isSixDrawBucket(buckets: SixesBucketMap, bucket: SixesBucket): boolean {
    return bucketCellIndices(buckets, bucket).length >= SIXES_SIX_DRAW_FULL_WINS;
}

export function bothRunsBucketsOnCard(buckets: SixesBucketMap): boolean {
    return (
        bucketCellIndices(buckets, "team_a_runs").length > 0 &&
        bucketCellIndices(buckets, "team_b_runs").length > 0
    );
}

/** Won cells across both runs buckets — used when a legacy card has both purple sides. */
export function mergedRunsWonCellIndices(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
): number[] {
    return [
        ...bucketWonCellIndices(buckets, outcomes, "team_a_runs"),
        ...bucketWonCellIndices(buckets, outcomes, "team_b_runs"),
    ];
}

export function isMergedRunsSlideReady(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
): boolean {
    return (
        bothRunsBucketsOnCard(buckets) &&
        mergedRunsWonCellIndices(buckets, outcomes).length >= SIXES_BUCKET_SLIDE_MIN_WINS
    );
}

export function bothPlayerBucketsOnCard(buckets: SixesBucketMap): boolean {
    return (
        bucketCellIndices(buckets, "team_a_player").length > 0 &&
        bucketCellIndices(buckets, "team_b_player").length > 0
    );
}

/** Won cells across both player buckets — same colour on the board, one slide family. */
export function mergedPlayerWonCellIndices(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
): number[] {
    return [
        ...bucketWonCellIndices(buckets, outcomes, "team_a_player"),
        ...bucketWonCellIndices(buckets, outcomes, "team_b_player"),
    ];
}

export function isMergedPlayerSlideReady(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
): boolean {
    return (
        bothPlayerBucketsOnCard(buckets) &&
        mergedPlayerWonCellIndices(buckets, outcomes).length >= SIXES_BUCKET_SLIDE_MIN_WINS
    );
}

function mergedPlayerClaimKind(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
): SixesClaimKind {
    return mergedSideClaimKind(mergedPlayerWonCellIndices(buckets, outcomes));
}

export function isBucketSlideReady(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
    bucket: SixesBucket,
): boolean {
    if (bothRunsBucketsOnCard(buckets) && SIXES_RUNS_SIDE_BUCKETS.includes(bucket)) {
        if (bucket === "team_b_runs") return false;
        return isMergedRunsSlideReady(buckets, outcomes);
    }
    if (bothPlayerBucketsOnCard(buckets) && SIXES_PLAYER_SIDE_BUCKETS.includes(bucket)) {
        if (bucket === "team_b_player") return false;
        return isMergedPlayerSlideReady(buckets, outcomes);
    }
    return bucketWonCount(buckets, outcomes, bucket) >= SIXES_BUCKET_SLIDE_MIN_WINS;
}

/**
 * Completion rank for ordering claims. `settleRank` maps a cell index → settle order (lower =
 * earlier); when absent we fall back to the cell index so ordering stays deterministic.
 *
 * - 4-draw (and 6-draw 6/6): rank = the LATEST winning cell (the cell that crossed the threshold).
 * - 6-draw 4/6: rank = the Nth-earliest winning cell where N = the row threshold (4).
 */
function completionRank(
    wonIndices: readonly number[],
    settleRank: ((idx: number) => number) | null,
    threshold: number,
    mode: "last" | "threshold",
): number {
    const rankOf = (idx: number) => (settleRank ? settleRank(idx) : idx);
    const ranks = wonIndices.map(rankOf).sort((a, b) => a - b);
    if (ranks.length === 0) return Number.POSITIVE_INFINITY;
    if (mode === "last") return ranks[ranks.length - 1]!;
    const i = Math.min(threshold, ranks.length) - 1;
    return ranks[Math.max(0, i)]!;
}

function shuffleInPlace<T>(items: T[], rng: SixesRng | null): T[] {
    if (!rng) return items;
    for (let i = items.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        [items[i], items[j]] = [items[j]!, items[i]!];
    }
    return items;
}

/**
 * Resolve the slot-level swaps that slide a bucket's winners onto a line, given the live layout.
 *
 * @param slotOf map original cell index → the slot it currently occupies (after earlier claims).
 * @returns swaps to apply (and which update `slotOf` / `originalAt` in the caller).
 */
function resolveClaimSwaps(
    lineId: SixesLineId,
    kind: SixesClaimKind,
    wonOriginalIndices: readonly number[],
    slotOf: ReadonlyMap<number, number>,
    rng: SixesRng | null,
): { target: number; moverSlot: number }[] {
    const targets = SIXES_LINE_TARGETS[lineId];
    const curSlot = (orig: number) => slotOf.get(orig) ?? orig;
    const winnerSlots = wonOriginalIndices.map(curSlot);
    const out: { target: number; moverSlot: number }[] = [];

    if (kind === "four_draw") {
        // A winner already sitting on a target stays put; every remaining target then takes a
        // winner — preferring one in the same row (the clean rook slide), else any leftover winner.
        // Preferring same-row keeps canonically rook-placed cards animating exactly as before, while
        // the fallback guarantees the line is fully filled with the bucket's colour even when the
        // card was NOT rook-placed (e.g. two winners share a row), instead of stranding a target.
        const usedMovers = new Set<number>();
        const assignedTargets = new Set<number>();
        for (const target of targets) {
            if (winnerSlots.includes(target)) {
                out.push({ target, moverSlot: target });
                usedMovers.add(target);
                assignedTargets.add(target);
            }
        }
        for (const target of targets) {
            if (assignedTargets.has(target)) continue;
            const targetRow = marketCellGridPosition(target).row;
            let moverSlot = winnerSlots.find(
                (slot) => !usedMovers.has(slot) && marketCellGridPosition(slot).row === targetRow,
            );
            if (moverSlot == null) moverSlot = winnerSlots.find((slot) => !usedMovers.has(slot));
            if (moverSlot != null) {
                out.push({ target, moverSlot });
                usedMovers.add(moverSlot);
                assignedTargets.add(target);
            }
        }
        return out;
    }

    // 6-draw row/column: winners already on a target slot stay; the rest fill remaining slots
    // (random when an rng is supplied — server-authoritative; stable otherwise for set counting).
    const targetSet = new Set<number>(targets);
    const remainingTargets: number[] = [];
    const used = new Set<number>();
    for (const target of targets) {
        if (winnerSlots.includes(target)) {
            out.push({ target, moverSlot: target });
            used.add(target);
        } else {
            remainingTargets.push(target);
        }
    }
    const remainingMovers = shuffleInPlace(
        winnerSlots.filter((slot) => !used.has(slot) && !targetSet.has(slot)),
        rng,
    );
    remainingTargets.forEach((target, i) => {
        const moverSlot = remainingMovers[i];
        if (moverSlot != null) out.push({ target, moverSlot });
    });
    return out;
}

/**
 * Build the ordered movement plan from buckets + outcomes.
 *
 * @param settleRank optional cell index → settle order (lower = earlier). Drives "first come first
 *   served" line claiming. Omit for set counting (count is order-invariant); pass it server-side
 *   so the persisted layout matches the real settlement order.
 * @param rng optional seeded RNG for the random slot assignment within 6-draw lines. Omit for the
 *   canonical (deterministic) layout used by set counting.
 */
export function buildSixesMovementPlan(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
    opts: { settleRank?: ((idx: number) => number) | null; rng?: SixesRng | null } = {},
): SixesMovementPlan {
    const settleRank = opts.settleRank ?? null;
    const rng = opts.rng ?? null;

    type Pending = { bucket: SixesBucket; kind: SixesClaimKind; rank: number; orderBucketIdx: number };
    const fourDraw: Pending[] = [];
    const sixRow: Pending[] = [];
    const sixCol: Pending[] = [];

    const splitRuns = bothRunsBucketsOnCard(buckets);
    let mergedRunsClaimed = false;
    const splitPlayers = bothPlayerBucketsOnCard(buckets);
    let mergedPlayersClaimed = false;

    for (const bucket of SIXES_BUCKETS) {
        const cells = bucketCellIndices(buckets, bucket);
        if (cells.length === 0) continue;
        if (splitRuns && SIXES_RUNS_SIDE_BUCKETS.includes(bucket)) continue;
        if (splitPlayers && SIXES_PLAYER_SIDE_BUCKETS.includes(bucket)) continue;

        const won = bucketWonCellIndices(buckets, outcomes, bucket);
        const orderBucketIdx = SIXES_BUCKETS.indexOf(bucket);
        const sixDraw = cells.length >= SIXES_SIX_DRAW_FULL_WINS;

        if (!sixDraw) {
            if (won.length >= SIXES_BUCKET_SLIDE_MIN_WINS) {
                fourDraw.push({
                    bucket,
                    kind: "four_draw",
                    rank: completionRank(won, settleRank, SIXES_BUCKET_SLIDE_MIN_WINS, "last"),
                    orderBucketIdx,
                });
            }
            continue;
        }

        if (won.length >= SIXES_SIX_DRAW_FULL_WINS) {
            sixCol.push({
                bucket,
                kind: "six_draw_col",
                rank: completionRank(won, settleRank, SIXES_SIX_DRAW_FULL_WINS, "last"),
                orderBucketIdx,
            });
        } else if (won.length >= SIXES_BUCKET_SLIDE_MIN_WINS) {
            sixRow.push({
                bucket,
                kind: "six_draw_row",
                rank: completionRank(won, settleRank, SIXES_BUCKET_SLIDE_MIN_WINS, "threshold"),
                orderBucketIdx,
            });
        }
    }

    if (splitRuns && isMergedRunsSlideReady(buckets, outcomes)) {
        const mergedWon = mergedRunsWonCellIndices(buckets, outcomes);
        const mergedKind = mergedRunsClaimKind(buckets, outcomes);
        const mergedPending: Pending = {
            bucket: SIXES_MERGED_RUNS_SLIDE_BUCKET,
            kind: mergedKind,
            rank: completionRank(
                mergedWon,
                settleRank,
                SIXES_BUCKET_SLIDE_MIN_WINS,
                mergedKind === "four_draw" ? "last" : "threshold",
            ),
            orderBucketIdx: SIXES_BUCKETS.indexOf(SIXES_MERGED_RUNS_SLIDE_BUCKET),
        };
        if (mergedKind === "four_draw") fourDraw.push(mergedPending);
        else sixRow.push(mergedPending);
        mergedRunsClaimed = true;
    }

    if (splitPlayers && isMergedPlayerSlideReady(buckets, outcomes)) {
        const mergedWon = mergedPlayerWonCellIndices(buckets, outcomes);
        const mergedKind = mergedPlayerClaimKind(buckets, outcomes);
        const mergedPending: Pending = {
            bucket: SIXES_MERGED_PLAYER_SLIDE_BUCKET,
            kind: mergedKind,
            rank: completionRank(
                mergedWon,
                settleRank,
                SIXES_BUCKET_SLIDE_MIN_WINS,
                mergedKind === "four_draw" ? "last" : "threshold",
            ),
            orderBucketIdx: SIXES_BUCKETS.indexOf(SIXES_MERGED_PLAYER_SLIDE_BUCKET),
        };
        if (mergedKind === "four_draw") fourDraw.push(mergedPending);
        else sixRow.push(mergedPending);
        mergedPlayersClaimed = true;
    }

    const byRank = (a: Pending, b: Pending) => a.rank - b.rank || a.orderBucketIdx - b.orderBucketIdx;
    fourDraw.sort(byRank);
    sixRow.sort(byRank);
    sixCol.sort(byRank);

    const jackpot = fourDraw.length >= SIXES_FOUR_DRAW_LINE_ORDER.length + 1;

    type Assigned = Pending & { lineId: SixesLineId };
    const assigned: Assigned[] = [];
    fourDraw.forEach((p, i) => {
        const lineId = SIXES_FOUR_DRAW_LINE_ORDER[i];
        if (lineId) assigned.push({ ...p, lineId });
    });
    sixRow.forEach((p, i) => {
        const lineId = SIXES_SIX_DRAW_ROW_ORDER[i];
        if (lineId) assigned.push({ ...p, lineId });
    });
    sixCol.forEach((p, i) => {
        const lineId = SIXES_SIX_DRAW_COL_ORDER[i];
        if (lineId) assigned.push({ ...p, lineId });
    });

    // Apply claims in completion order against an evolving layout so later claims slide from where
    // cells actually sit — the universal swap rule then composes correctly.
    assigned.sort((a, b) => a.rank - b.rank || a.orderBucketIdx - b.orderBucketIdx);

    const slotOf = new Map<number, number>(); // original cell → current slot
    const originalAt = new Map<number, number>(); // current slot → original cell
    const curSlot = (orig: number) => slotOf.get(orig) ?? orig;
    const origAt = (slot: number) => originalAt.get(slot) ?? slot;

    const claims: SixesLineClaim[] = [];
    for (const a of assigned) {
        const won =
            mergedRunsClaimed &&
            a.bucket === SIXES_MERGED_RUNS_SLIDE_BUCKET &&
            splitRuns
                ? mergedRunsWonCellIndices(buckets, outcomes)
                : mergedPlayersClaimed &&
                    a.bucket === SIXES_MERGED_PLAYER_SLIDE_BUCKET &&
                    splitPlayers
                  ? mergedPlayerWonCellIndices(buckets, outcomes)
                  : bucketWonCellIndices(buckets, outcomes, a.bucket);
        const moves = resolveClaimSwaps(a.lineId, a.kind, won, slotOf, rng);
        const swaps: SixesSlideSwap[] = [];
        for (const { target, moverSlot } of moves) {
            if (moverSlot === target) continue;
            swaps.push({ from: moverSlot, to: target });
            // Swap the originals sitting at the two slots, keeping both maps in sync.
            const movingOrig = origAt(moverSlot);
            const displacedOrig = origAt(target);
            slotOf.set(movingOrig, target);
            slotOf.set(displacedOrig, moverSlot);
            originalAt.set(target, movingOrig);
            originalAt.set(moverSlot, displacedOrig);
        }
        claims.push({ bucket: a.bucket, lineId: a.lineId, kind: a.kind, rank: a.rank, swaps });
    }

    return { claims, jackpot };
}

/**
 * Swaps that slide a bucket's winners onto a claimed line from their HOME positions (identity
 * layout). Used by the live grid, whose cells animate via absolute-from-home offsets (content is
 * never permuted), so `from` is always a winner's home index.
 */
export function sixesLineClaimSwaps(
    lineId: SixesLineId,
    kind: SixesClaimKind,
    wonHomeIndices: readonly number[],
    rng: SixesRng | null = null,
): SixesSlideSwap[] {
    const moves = resolveClaimSwaps(lineId, kind, wonHomeIndices, new Map<number, number>(), rng);
    return moves
        .filter((m) => m.moverSlot !== m.target)
        .map((m) => ({ from: m.moverSlot, to: m.target }));
}

/** Flatten a movement plan into ordered directional swaps (cell at `from` slides to `to`). */
export function movementPlanSwaps(plan: SixesMovementPlan): SixesSlideSwap[] {
    const swaps: SixesSlideSwap[] = [];
    for (const claim of plan.claims) swaps.push(...claim.swaps);
    return swaps;
}

/** Deterministic 0..1 RNG (mulberry32) so a card's random slot assignment is stable. */
export function seededRng(seed: number): SixesRng {
    let a = seed >>> 0;
    return function next(): number {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Stable 32-bit seed from a card id (number or string), mirroring the backend. */
export function seedFromCardId(cardId: number | string): number {
    const s = String(cardId ?? "");
    let h = 2166136261 >>> 0;
    for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

/** Apply directional swaps to an array, in order (each swaps the values at the two slots). */
export function applySixesSwaps<T>(values: readonly T[], swaps: readonly SixesSlideSwap[]): T[] {
    const next = [...values];
    for (const { from, to } of swaps) {
        if (from === to) continue;
        const tmp = next[from]!;
        next[from] = next[to]!;
        next[to] = tmp;
    }
    return next;
}
